/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { serializeError } from "serialize-error";
import * as tiff from "tiff";

import { FileStatus } from "@vienna/common";

import { API } from "src/classes/api";
import { clientLogger } from "src/lib/logging/datadog";

export class FileUtils {
  /* -------------------------------------------------- */
  /*                    File Upload                    */
  /* -------------------------------------------------- */
  public static async uploadFileWithStreaming(
    file: File,
    bucketName: string,
    streamingCallback: (status: FileStatus, percentComplete?: number) => void
  ): Promise<void> {
    try {
      const url = await API.files.getSignedURL(file.name, bucketName);
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        // calculate percent from "length" so length must be computable
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / file.size) * 100;
          streamingCallback(FileStatus.Uploading, percentComplete);
        }
      };
      // this basically means the upload is done and either succeeeded or failed
      xhr.onreadystatechange = () => {
        // 4 means the upload is done
        if (xhr.readyState === 4) {
          // 200 means the upload failed
          if (xhr.status !== 200) {
            streamingCallback(FileStatus.Failed);
            throw new Error(`Request failed with error code ${xhr.status}`);
          }
          streamingCallback(FileStatus.Uploaded, 100);
        }
      };

      // call the upload functions now that listeners are attached
      xhr.open("PUT", url);
      xhr.send(file);
    } catch (e) {
      streamingCallback(FileStatus.Failed, 0);
    }
  }

  public static async uploadFile(file: File, bucketName: string): Promise<string> {
    const url = await API.files.getSignedURL(file.name, bucketName);
    fetch(url, { method: "PUT", body: file });
    return url;
  }

  /* -------------------------------------------------- */
  /*                   File Download                    */
  /* -------------------------------------------------- */
  public static async downloadImageFromURL(url: string, originalFilename: string): Promise<File> {
    const res = await API.files.download(url);
    const fileType = res.headers["content-type"];
    if (!fileType) {
      throw new Error("Failed to extract file type, cannot download image");
    }
    // Extract the file extension from the content type
    const extension = fileType.split("/")[1];

    // Convert base64 string to Blob
    const byteCharacters = window.atob(res.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });

    const file = new File([blob], `${originalFilename}.${extension}`, { type: fileType });

    return file;
  }

  /* -------------------------------------------------- */
  /*                    File Name                    */
  /* -------------------------------------------------- */
  public static renameFile = (file: File, newName: string): File => {
    const extension = file.name.split(".").pop();
    const newNameWithExtension = `${newName}.${extension}`;
    return new File([file], newNameWithExtension, {
      type: file.type,
    });
  };

  public static readURLFromFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }

  /* -------------------------------------------------- */
  /*                    File Size                    */
  /* -------------------------------------------------- */
  public static async getPixelSizeOfImageFile(image: File): Promise<{ width: number; height: number } | null> {
    const imgDims = new Image();
    imgDims.src = window.URL.createObjectURL(image);
    let naturalWidth;
    let naturalHeight;
    // only safari and IE support for tiff decoding, use a library for this file type
    try {
      if (image.type === "image/tiff") {
        const tiffImage = tiff.decode(await image.arrayBuffer());
        naturalWidth = tiffImage[0].width;
        naturalHeight = tiffImage[0].height;
      } else {
        await imgDims.decode();
        naturalWidth = imgDims.naturalWidth;
        naturalHeight = imgDims.naturalHeight;
      }
    } catch (_err) {
      clientLogger.error("Error decoding image source", {
        area: "uploads",
        fileExtension: image.type,
        size: FileUtils.getReadableFileSizeFromBytes(image.size),
        inner: serializeError(_err),
      });
      return null;
    }
    const aspectRatio = naturalHeight / naturalWidth;
    let width;
    let height;
    if (aspectRatio < 1) {
      // @later what is all of this...
      // function that gradually approaches horizontal asymptote of y=600
      // we're using this to calculate the default size of an image if the
      // largest dimension is >500px
      width = Math.min(naturalWidth, Math.round((600 * naturalWidth - 400) / (naturalWidth + 99)));
      height = width * aspectRatio;
    } else {
      height = Math.min(naturalHeight, Math.round((600 * naturalHeight - 400) / (naturalHeight + 99)));
      width = height / aspectRatio;
    }
    return { width, height };
  }

  public static getReadableFileSizeFromBytes(bytes: number): string {
    if (!bytes) {
      return "";
    }
    const thresh = 1000;
    const units = ["B", "kB", "MB", "GB"];
    let u = 0;
    const r = 10;
    while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1) {
      bytes /= thresh;
      ++u;
    }
    return `${bytes.toFixed(1)} ${units[u]}`;
  }
}
